/**
 * Um dado da ação, montado pelo tipo dele, sem expressão nem aspas:
 *
 * - texto: escreve-se normal; a tela põe as aspas;
 * - conversa, oportunidade: escolhe-se do evento ("A conversa do evento");
 * - pessoa, etapa, motivo, situação: a lista do CRM (e, na pessoa, quem o
 *   evento traz);
 * - opções do menu e listas: um campo por item, com "+" para mais um;
 * - prazo: "Em 1 dia", contado de quando a automação roda;
 * - onde pendurar a tarefa e os campos do card: escolhas e textos.
 *
 * O que muda de ação para ação (os campos do card, o nome dos itens, o texto
 * longo, o que não aceita valor fixo) vem do catálogo do CRM, não daqui. O dado
 * do cliente (o contato, o nome dele) não aceita valor fixo: só vem do evento.
 *
 * "Usar uma expressão" fica para o que as escolhas não cobrem. A expressão que
 * não foi montada aqui abre como texto, como está.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { BlocosDoEditor } from '@cliqcrm/contracts';
import { expressaoDoValor, textoFixo } from '@/shared/lib/valor-fixo';
import { Botao, Campo, CampoArea, CampoSelecao, Icone } from '@/shared/ui';
import {
  MAXIMO_DE_OPCOES,
  PRAZOS,
  ROTULO_DA_LISTA,
  ROTULO_DO_BOTAO,
  caminhoDoAlvo,
  caminhoDoContato,
  camposDaExpressao,
  deQuem,
  expressaoDoContato,
  expressaoDaLista,
  expressaoDoAlvo,
  expressaoDoMenu,
  expressaoDoPrazo,
  expressaoDosCampos,
  horasDoPrazo,
  idDaOpcao,
  opcoesDoMenu,
  textosDaLista,
  type OpcaoDoMenu,
} from '../lib/parametros';
import { comPreposicao } from '../lib/referencias';
import { ComCamposDoEvento } from './inserir-campo';
import { ParametroComOpcoes } from './parametro-com-opcoes';

type Parametro = BlocosDoEditor['acoes'][number]['parametros'][number];
type Referencia = BlocosDoEditor['eventos'][number]['referencias'][number];

export function ValorDoParametro({
  parametro: p,
  referencias,
  contexto,
  valor,
  aoMudar,
}: {
  parametro: Parametro;
  /** O que o evento do início traz, pelo nome. */
  referencias: readonly Referencia[];
  /** Os caminhos do evento, para o "Inserir" da expressão. */
  contexto: readonly string[];
  valor: string;
  aoMudar: (valor: string) => void;
}): ReactNode {
  const [pediuExpressao, definirPediuExpressao] = useState(false);
  const rotulo = p.obrigatorio ? p.titulo : `${p.titulo} (opcional)`;
  const doEvento = (tipo: Referencia['tipo']) => referencias.filter((r) => r.tipo === tipo);

  const guiado = ((): ReactNode | null => {
    // A lista do CRM: a etapa, a pessoa (com quem o evento traz), o motivo, a situação.
    if (daLista(p)) {
      return (
        <ParametroComOpcoes
          rotulo={rotulo}
          opcoes={p.opcoes ?? []}
          opcaoNula={p.opcao_nula}
          vazio={p.vazio}
          seVazia={p.se_vazia}
          doEvento={p.tipo === 'pessoa' ? doEvento('pessoa') : []}
          campos={contexto}
          valor={valor}
          aoMudar={aoMudar}
        />
      );
    }
    switch (p.tipo) {
      case 'conversa':
      case 'oportunidade': {
        /*
         * A oportunidade também pela conversa do evento: o CRM acha a vinculada
         * a ela, ou a que o cliente dela tem em aberto. O evento de conversa não
         * traz oportunidade, e o id da conversa no lugar dela nunca acharia nada.
         */
        const daqui = [
          ...doEvento(p.tipo).map((r) => ({ valor: r.caminho, titulo: r.titulo })),
          ...(p.tipo === 'oportunidade'
            ? doEvento('conversa').map((r) => ({
                valor: expressaoDoContato(r.caminho),
                titulo: `A oportunidade ${deQuem(r.titulo)}`,
              }))
            : []),
        ];
        // O id da conversa no lugar da oportunidade: o engano comum, e ele nunca acha nada.
        const conversaNoLugar =
          p.tipo === 'oportunidade' && doEvento('conversa').some((r) => r.caminho === valor.trim());
        // A que não é do evento (uma expressão escrita) abre como texto.
        if (valor.trim() && !conversaNoLugar && !daqui.some((r) => r.valor === normalizada(valor)))
          return null;
        return (
          <DoEvento
            rotulo={rotulo}
            tipo={p.tipo}
            obrigatorio={p.obrigatorio}
            referencias={daqui}
            valor={valor}
            aoMudar={aoMudar}
            {...(conversaNoLugar
              ? {
                  aviso:
                    'Aqui está o id da conversa, que não acha a oportunidade. Escolha "A oportunidade da conversa do evento".',
                }
              : {})}
          />
        );
      }
      case 'texto': {
        // O dado do cliente não vai fixo na automação: só do evento, por expressão.
        if (p.valor_fixo === false) return null;
        const texto = textoFixo(valor);
        if (valor.trim() && texto === null) return null;
        const mudar = (t: string): void => aoMudar(t.trim() ? expressaoDoValor(t) : '');
        return p.longo ? (
          <CampoArea
            rotulo={rotulo}
            rows={3}
            value={texto ?? ''}
            onChange={(e) => mudar(e.target.value)}
          />
        ) : (
          <Campo rotulo={rotulo} value={texto ?? ''} onChange={(e) => mudar(e.target.value)} />
        );
      }
      case 'opcoes_do_menu': {
        const opcoes = opcoesDoMenu(valor);
        return opcoes ? <OpcoesDoMenu opcoes={opcoes} aoMudar={aoMudar} /> : null;
      }
      case 'lista_de_textos': {
        const textos = textosDaLista(valor);
        return textos ? (
          <ListaDeTextos
            rotulo={rotulo}
            item={p.item ?? 'Item'}
            opcoes={p.opcoes ?? []}
            {...(p.opcoes?.length === 0 && p.se_vazia ? { ajuda: p.se_vazia } : {})}
            textos={textos}
            aoMudar={(t) => aoMudar(expressaoDaLista(t))}
          />
        ) : null;
      }
      case 'data': {
        const horas = horasDoPrazo(valor);
        if (valor.trim() && horas === null) return null;
        return (
          <CampoSelecao
            rotulo={rotulo}
            ajuda="Contado a partir de quando a automação roda."
            value={horas === null ? '' : String(horas)}
            onChange={(e) =>
              aoMudar(e.target.value ? expressaoDoPrazo(Number(e.target.value)) : '')
            }
          >
            <option value="">Sem prazo</option>
            {PRAZOS.map((x) => (
              <option key={x.horas} value={String(x.horas)}>
                {x.rotulo}
              </option>
            ))}
          </CampoSelecao>
        );
      }
      case 'alvo_da_tarefa': {
        const caminho = caminhoDoAlvo(valor);
        if (valor.trim() && caminho === null) return null;
        const oportunidades = doEvento('oportunidade');
        return (
          <CampoSelecao
            rotulo={rotulo}
            value={caminho ?? ''}
            onChange={(e) => aoMudar(e.target.value ? expressaoDoAlvo(e.target.value) : '')}
            {...(oportunidades.length === 0
              ? { ajuda: 'Este início não traz uma oportunidade: a tarefa fica solta.' }
              : {})}
          >
            <option value="">Solta, sem pendurar</option>
            {oportunidades.map((r) => (
              <option key={r.caminho} value={r.caminho}>
                {comPreposicao('em', r.titulo)}
              </option>
            ))}
          </CampoSelecao>
        );
      }
      case 'contato': {
        // O contato da conversa do evento; outro jeito (um número, uma coluna) é expressão.
        const conversas = doEvento('conversa');
        const caminho = caminhoDoContato(valor);
        if (conversas.length === 0 || (valor.trim() && caminho === null)) return null;
        return (
          <CampoSelecao
            rotulo={rotulo}
            ajuda="O cliente que já está ligado à conversa no CRM. A conversa sem cliente ligado não serve aqui."
            value={caminho ?? ''}
            onChange={(e) => aoMudar(e.target.value ? expressaoDoContato(e.target.value) : '')}
          >
            <option value="">{p.obrigatorio ? 'Escolha' : 'Sem contato'}</option>
            {conversas.map((r) => (
              <option key={r.caminho} value={r.caminho}>
                O contato {deQuem(r.titulo)}
              </option>
            ))}
          </CampoSelecao>
        );
      }
      case 'campos': {
        const campos = camposDaExpressao(valor);
        if (!p.subcampos || campos === null) return null;
        return (
          <fieldset className="editor-do-mapa__condicao">
            <legend className="editor-do-mapa__condicao-titulo">{rotulo}</legend>
            <p className="editor-do-mapa__painel-ajuda">
              Texto: vai exatamente como estiver escrito aqui.
            </p>
            {p.subcampos.map((c) => (
              <Campo
                key={c.chave}
                rotulo={`${c.titulo} (texto, opcional)`}
                placeholder="Escreva o texto"
                value={campos[c.chave] ?? ''}
                onChange={(e) =>
                  aoMudar(expressaoDosCampos({ ...campos, [c.chave]: e.target.value }))
                }
              />
            ))}
          </fieldset>
        );
      }
      default:
        return null;
    }
  })();

  if (guiado !== null && !pediuExpressao) {
    return (
      <div className="editor-do-mapa__condicao">
        {guiado}
        {/* A lista do CRM já traz o próprio "Usar uma expressão". */}
        {!daLista(p) && (
          <Botao variante="fantasma" miudo onClick={() => definirPediuExpressao(true)}>
            Usar uma expressão
          </Botao>
        )}
      </div>
    );
  }

  return (
    <div className="editor-do-mapa__condicao">
      <ComCamposDoEvento campos={contexto} valor={valor} aoMudar={aoMudar}>
        {(ref) => (
          <Campo
            ref={ref}
            rotulo={rotulo}
            ajuda={
              p.tipo === 'conversa' || p.tipo === 'oportunidade'
                ? `Uma expressão sobre o evento que dá o id ${p.tipo === 'conversa' ? 'da conversa' : 'da oportunidade'}. ${p.tipo === 'oportunidade' ? 'O id da conversa aqui não acha a oportunidade: volte às escolhas e use "A oportunidade da conversa do evento".' : ''}`.trim()
                : p.valor_fixo === false
                  ? 'Dado do cliente: não fica escrito na automação. Vem de uma expressão sobre o evento (um campo dele); deixe vazio para não informar.'
                  : 'Uma expressão sobre o evento. Texto fixo vai entre aspas, como "Olá".'
            }
            spellCheck={false}
            value={valor}
            onChange={(e) => aoMudar(e.target.value)}
          />
        )}
      </ComCamposDoEvento>
      {(pediuExpressao || temEscolhas(p, doEvento('conversa').length > 0)) && (
        <Botao
          variante="fantasma"
          miudo
          onClick={() => {
            definirPediuExpressao(false);
            // Volta às escolhas só se elas leem o que está escrito; senão, recomeça.
            if (guiado === null) aoMudar('');
          }}
        >
          Voltar às escolhas
        </Botao>
      )}
    </div>
  );
}

/** Um valor só, escolhido da lista do CRM. A lista de textos usa a lista em cada item. */
function daLista(p: Parametro): boolean {
  return p.tipo !== 'lista_de_textos' && Boolean(p.opcoes || p.opcao_nula);
}

/** O parâmetro que o editor sabe montar sem expressão: o "Voltar às escolhas" leva a elas. */
function temEscolhas(p: Parametro, comConversa: boolean): boolean {
  if (daLista(p)) return false; // a lista do CRM tem o próprio botão
  if (p.tipo === 'texto') return p.valor_fixo !== false;
  if (p.tipo === 'contato') return comConversa;
  if (p.tipo === 'campos') return Boolean(p.subcampos);
  return [
    'conversa',
    'oportunidade',
    'opcoes_do_menu',
    'lista_de_textos',
    'data',
    'alvo_da_tarefa',
  ].includes(p.tipo);
}

function DoEvento({
  rotulo,
  tipo,
  obrigatorio,
  referencias,
  valor,
  aoMudar,
  aviso,
}: {
  rotulo: string;
  tipo: 'conversa' | 'oportunidade';
  obrigatorio: boolean;
  referencias: readonly { readonly valor: string; readonly titulo: string }[];
  valor: string;
  aoMudar: (valor: string) => void;
  /** O que está errado no valor salvo, no lugar da ajuda. */
  aviso?: string;
}): ReactNode {
  /*
   * O bloco novo, com um só do evento, já vem com ele: é quase sempre ele. Uma
   * vez só, ao abrir, e só no obrigatório: o opcional deixado vazio fica vazio,
   * e quem limpa o campo não o vê voltar.
   */
  const jaPreencheu = useRef(false);
  const unica = referencias.length === 1 ? referencias[0]!.valor : null;
  useEffect(() => {
    if (jaPreencheu.current) return;
    jaPreencheu.current = true;
    if (obrigatorio && !valor.trim() && unica) aoMudar(unica);
  }, [obrigatorio, valor, unica, aoMudar]);

  const escolhida = referencias.find((r) => r.valor === normalizada(valor));
  return (
    <CampoSelecao
      rotulo={rotulo}
      value={escolhida?.valor ?? ''}
      onChange={(e) => aoMudar(e.target.value)}
      {...(aviso
        ? { ajuda: aviso }
        : referencias.length === 0
          ? {
              ajuda:
                tipo === 'conversa'
                  ? 'Este início não traz uma conversa: troque o início, ou use uma expressão.'
                  : 'Este início não traz uma oportunidade: troque o início, ou use uma expressão.',
            }
          : {})}
    >
      <option value="">Escolha</option>
      {referencias.map((r) => (
        <option key={r.valor} value={r.valor}>
          {r.titulo}
        </option>
      ))}
    </CampoSelecao>
  );
}

/** A expressão como o editor a monta: `'conversa:' & caminho`, sem espaço sobrando. */
function normalizada(valor: string): string {
  const caminho = caminhoDoContato(valor);
  return caminho ? expressaoDoContato(caminho) : valor.trim();
}

/** Uma linha do editor das opções: o id só existe para a opção que já foi salva. */
type LinhaDoMenu = { readonly id: string | null; readonly rotulo: string };

/**
 * Os ids das opções: a salva guarda o dela (um caso da Escolha pode usá-lo); a
 * nova ganha o do rótulo, sem repetir nenhum outro da lista. Calculado a cada
 * mudança sobre o rótulo inteiro, e não na primeira letra digitada.
 */
function comIds(linhas: readonly LinhaDoMenu[]): OpcaoDoMenu[] {
  const usados = linhas.flatMap((l) => (l.id ? [l.id] : []));
  return linhas.map((l) => {
    if (l.id) return { id: l.id, rotulo: l.rotulo };
    const id = l.rotulo.trim() ? idDaOpcao(l.rotulo, usados) : '';
    if (id) usados.push(id);
    return { id, rotulo: l.rotulo };
  });
}

function OpcoesDoMenu({
  opcoes,
  aoMudar,
}: {
  opcoes: readonly OpcaoDoMenu[];
  aoMudar: (valor: string) => void;
}): ReactNode {
  /*
   * As linhas ficam aqui: a opção recém-acrescentada ainda não tem rótulo, não
   * vira expressão, e sumiria se a lista saísse só dela.
   */
  const [linhas, definirLinhas] = useState<readonly LinhaDoMenu[]>(() =>
    opcoes.length > 0
      ? opcoes.map((o) => ({ id: o.id, rotulo: o.rotulo }))
      : [
          { id: null, rotulo: '' },
          { id: null, rotulo: '' },
        ],
  );
  const mudar = (novas: readonly LinhaDoMenu[]): void => {
    definirLinhas(novas);
    aoMudar(expressaoDoMenu(comIds(novas)));
  };
  const viraBotao = linhas.filter((o) => o.rotulo.trim()).length <= 3;
  const limite = viraBotao ? ROTULO_DO_BOTAO : ROTULO_DA_LISTA;

  return (
    <fieldset className="editor-do-mapa__condicao">
      <legend className="editor-do-mapa__condicao-titulo">Opções</legend>
      <p className="editor-do-mapa__painel-ajuda">
        {viraBotao
          ? `Até 3 opções viram botões no WhatsApp, com até ${ROTULO_DO_BOTAO} letras.`
          : `De 4 a 10 opções, viram uma lista, com até ${ROTULO_DA_LISTA} letras.`}
      </p>
      {linhas.map((o, i) => (
        <div key={i} className="editor-do-mapa__regra">
          <Campo
            rotulo={`Opção ${i + 1}`}
            maxLength={ROTULO_DA_LISTA}
            value={o.rotulo}
            ajuda={`${o.rotulo.length}/${limite}`}
            {...(o.rotulo.length > limite
              ? { erro: `Passa de ${limite} letras: o WhatsApp corta.` }
              : {})}
            onChange={(e) =>
              mudar(linhas.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)))
            }
          />
          {linhas.length > 1 && (
            <Botao
              variante="fantasma"
              icone
              miudo
              rotulo={`Tirar a opção ${i + 1}`}
              aria-label={`Tirar a opção ${i + 1}`}
              onClick={() => mudar(linhas.filter((_, j) => j !== i))}
            >
              <Icone nome="lixeira" />
            </Botao>
          )}
        </div>
      ))}
      <div className="editor-do-mapa__condicao-acoes">
        <Botao
          variante="secundario"
          miudo
          onClick={() => mudar([...linhas, { id: null, rotulo: '' }])}
          desabilitado={linhas.length >= MAXIMO_DE_OPCOES}
          motivo={`São no máximo ${MAXIMO_DE_OPCOES} opções.`}
        >
          <Icone nome="mais" />
          Adicionar opção
        </Botao>
      </div>
    </fieldset>
  );
}

function ListaDeTextos({
  rotulo,
  item,
  opcoes,
  ajuda,
  textos,
  aoMudar,
}: {
  rotulo: string;
  item: string;
  /** Os valores da organização (as etiquetas): cada item vira uma escolha. */
  opcoes: readonly { valor: string; rotulo: string }[];
  ajuda?: string;
  textos: readonly string[];
  aoMudar: (textos: string[]) => void;
}): ReactNode {
  const [linhas, definirLinhas] = useState<readonly string[]>(() =>
    textos.length > 0 ? textos : [''],
  );
  const mudar = (novas: readonly string[]): void => {
    definirLinhas(novas);
    aoMudar([...novas]);
  };
  return (
    <fieldset className="editor-do-mapa__condicao">
      <legend className="editor-do-mapa__condicao-titulo">{rotulo}</legend>
      {ajuda && <p className="editor-do-mapa__painel-ajuda">{ajuda}</p>}
      {linhas.map((t, i) => (
        <div key={i} className="editor-do-mapa__regra">
          {opcoes.length > 0 ? (
            <CampoSelecao
              rotulo={`${item} ${i + 1}`}
              value={t}
              onChange={(e) => mudar(linhas.map((x, j) => (j === i ? e.target.value : x)))}
            >
              <option value="">Escolha</option>
              {/* O que foi salvo e saiu do catálogo continua aparecendo, para não sumir calado. */}
              {t && !opcoes.some((o) => o.valor === t) && <option value={t}>{t}</option>}
              {opcoes.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </CampoSelecao>
          ) : (
            <Campo
              rotulo={`${item} ${i + 1}`}
              value={t}
              onChange={(e) => mudar(linhas.map((x, j) => (j === i ? e.target.value : x)))}
            />
          )}
          {linhas.length > 1 && (
            <Botao
              variante="fantasma"
              icone
              miudo
              rotulo={`Tirar ${item.toLowerCase()} ${i + 1}`}
              aria-label={`Tirar ${item.toLowerCase()} ${i + 1}`}
              onClick={() => mudar(linhas.filter((_, j) => j !== i))}
            >
              <Icone nome="lixeira" />
            </Botao>
          )}
        </div>
      ))}
      <div className="editor-do-mapa__condicao-acoes">
        <Botao variante="secundario" miudo onClick={() => mudar([...linhas, ''])}>
          <Icone nome="mais" />
          Adicionar {item.toLowerCase()}
        </Botao>
      </div>
    </fieldset>
  );
}
